import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import { ChartCard } from "../../componentes/chart/CardChart";
import CardIndicador from "../../components/solicitacoes/Shared/CardIndicador";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import RenovacaoGdmService from "../../services/resource/renovacaoGdmService";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import { formatarDataHora } from "../../utils/solicitacoes/formatters";
import { AGRUPAMENTOS, SITUACAO_POR_VALOR, SITUACOES, agrupar, chaveEtapa, contarPorEtapa, resumir } from "../../utils/solicitacoes/conclusaoGdm";
import { ETAPAS_RENOVACAO_GDM } from "../../components/solicitacoes/FormEtapas/renovacaogdm/NovaSolicitacaoRenovacaoGdmForm";
import TabelaEntriesGrafico from "../../components/solicitacoes/Dashboard/TabelaEntriesGrafico";

const service = new RenovacaoGdmService();

const FILTROS_VAZIOS = { dataInicial: "", dataFinal: "", cliente: "", rota: null, cidade: null, canal: null, gdmStatus: null, logomarca: null };
const OPCOES_VAZIAS = { rotas: [], cidades: [], canais: [], gdmStatus: [], logomarcas: [] };
const LIMITE_GRUPOS_GRAFICO = 25;

const mensagemErro = (e, padrao) => e?.response?.data?.message || padrao;
const formatarPercentual = v => `${(v ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

function SituacaoBadge({ situacao }) {
    const info = SITUACAO_POR_VALOR[situacao];
    if (!info) return "-";
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ backgroundColor: `${info.cor}1F`, color: info.cor }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: info.cor }} />
            {info.singular}
        </span>
    );
}

/** Barra empilhada com a proporção de cada situação no total. */
function BarraProgresso({ resumo }) {
    if (!resumo.total) return null;
    return (
        <div className="bg-card border border-border rounded-lg shadow-card p-4 flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">Andamento da base</h2>
                <span className="text-xs text-muted-foreground">
                    {resumo.concluidas.toLocaleString("pt-BR")} de {resumo.total.toLocaleString("pt-BR")} GDMs com o processo concluído
                </span>
            </div>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
                {SITUACOES.map(s => resumo.contagem[s.value] > 0 && (
                    <div key={s.value} title={`${s.label}: ${resumo.contagem[s.value]}`}
                        style={{ width: `${(resumo.contagem[s.value] / resumo.total) * 100}%`, backgroundColor: s.cor }} />
                ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {SITUACOES.map(s => (
                    <span key={s.value} className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.cor }} />
                        {s.label}: {formatarPercentual(resumo.total ? (resumo.contagem[s.value] / resumo.total) * 100 : 0)}
                    </span>
                ))}
            </div>
        </div>
    );
}

export default function RelatorioConclusaoGdm() {
    const navigate = useNavigate();
    const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
    const [opcoes, setOpcoes] = useState(OPCOES_VAZIAS);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erroAcesso, setErroAcesso] = useState(null);

    const [situacao, setSituacao] = useState(null);
    const [agrupamento, setAgrupamento] = useState("rota");
    const [viewGrupo, setViewGrupo] = useState("chart");
    const [etapa, setEtapa] = useState(null);
    const [viewEtapa, setViewEtapa] = useState("chart");

    const pesquisar = useCallback((filtrosBusca) => {
        setCarregando(true);
        service.conclusaoBaseGdm({ ...filtrosBusca, cliente: filtrosBusca.cliente.trim() })
            .then(res => {
                setDados(res.data);
                setErroAcesso(null);
            })
            .catch(e => {
                if (e?.response?.status === 403) setErroAcesso(mensagemErro(e, "Sem permissão para ver este relatório."));
                else toast.error(mensagemErro(e, "Erro ao gerar o relatório de conclusão."));
            })
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => {
        document.title = "MyDash | Conclusão da Renovação de GDM";
        service.opcoesBaseGdm().then(res => setOpcoes({ ...OPCOES_VAZIAS, ...res.data })).catch(() => setOpcoes(OPCOES_VAZIAS));
        pesquisar(FILTROS_VAZIOS);
    }, [pesquisar]);

    const gdms = useMemo(() => dados?.gdms || [], [dados]);
    const resumo = useMemo(() => resumir(gdms), [gdms]);
    const ordemAgrupamento = AGRUPAMENTOS.find(a => a.value === agrupamento)?.ordem;
    const grupos = useMemo(() => agrupar(gdms, agrupamento, { ordem: ordemAgrupamento }), [gdms, agrupamento, ordemAgrupamento]);
    const porEtapa = useMemo(() => contarPorEtapa(gdms, ETAPAS_RENOVACAO_GDM), [gdms]);
    const gdmsFiltradas = useMemo(() => gdms.filter(g => (!situacao || g.situacao === situacao)
        && (!etapa || chaveEtapa(g, ETAPAS_RENOVACAO_GDM) === etapa)), [gdms, situacao, etapa]);
    const etapaSelecionada = porEtapa.find(b => b.chave === etapa);
    const labelAgrupamento = AGRUPAMENTOS.find(a => a.value === agrupamento)?.label;

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    function limparFiltros() {
        setFiltros(FILTROS_VAZIOS);
        setSituacao(null);
        setEtapa(null);
        pesquisar(FILTROS_VAZIOS);
    }

    const kpis = [
        { label: "GDMs na base", value: resumo.total.toLocaleString("pt-BR"), icon: "fa fa-snowflake", situacao: null },
        ...SITUACOES.map(s => ({ label: s.label, value: (resumo.contagem[s.value] || 0).toLocaleString("pt-BR"), icon: s.icon, iconClassName: s.iconClassName, situacao: s.value })),
    ];

    // Por total, o gráfico mostra só os maiores; na ordem do nome (rotas), mostra todos — cortar deixaria buracos na sequência.
    const limitarGrafico = ordemAgrupamento !== "nome" && grupos.length > LIMITE_GRUPOS_GRAFICO;
    const gruposGrafico = limitarGrafico ? grupos.slice(0, LIMITE_GRUPOS_GRAFICO) : grupos;
    const graficoOptions = {
        chart: { stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 2 } },
        xaxis: { categories: gruposGrafico.map(g => g.grupo), labels: { style: { fontSize: "11px" } } },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        colors: SITUACOES.map(s => s.cor),
        legend: { position: "top", fontSize: "12px" },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
    };
    const graficoSeries = SITUACOES.map(s => ({ name: s.label, data: gruposGrafico.map(g => g[s.value]) }));

    // Etapas do fluxo em tons de azul; "Concluída" e "Sem solicitação" com as cores das situações.
    const COR_ETAPA = { CONCLUIDA: SITUACAO_POR_VALOR.RENOVADA.cor, SEM_SOLICITACAO: SITUACAO_POR_VALOR.SEM_SOLICITACAO.cor, OUTRA: "#94A3B8" };
    const TONS_ETAPA = ["#93C5FD", "#3B82F6", "#1D4ED8", "#1E3A8A"];
    const etapaOptions = {
        chart: {
            toolbar: { show: false },
            // Clicar numa barra filtra a tabela de GDMs por aquela etapa (clicar de novo limpa).
            events: {
                dataPointSelection: (_e, _ctx, { dataPointIndex }) => {
                    const chave = porEtapa[dataPointIndex]?.chave;
                    if (chave) setEtapa(atual => (atual === chave ? null : chave));
                },
            },
        },
        plotOptions: { bar: { distributed: true, borderRadius: 4, columnWidth: "55%" } },
        colors: porEtapa.map((b, i) => COR_ETAPA[b.chave] || TONS_ETAPA[i % TONS_ETAPA.length]),
        xaxis: { categories: porEtapa.map(b => b.label), labels: { style: { fontSize: "11px" }, trim: true } },
        legend: { show: false },
        dataLabels: { enabled: true, style: { fontSize: "11px" } },
        tooltip: { y: { formatter: v => `${v.toLocaleString("pt-BR")} GDM(s)` } },
        states: { active: { filter: { type: "none" } } },
    };

    const colunasGrupo = [
        { key: "grupo", label: labelAgrupamento },
        { key: "total", label: "GDMs", center: true },
        ...SITUACOES.map(s => ({ key: s.value, label: s.label, center: true })),
        { key: "percentualConcluido", label: "% Concluído", center: true, render: formatarPercentual },
        { key: "percentualRenovado", label: "% Renovado", center: true, render: formatarPercentual },
    ];

    const colunasGdm = [
        { key: "situacao", label: "Situação", center: true, render: v => <SituacaoBadge situacao={v} /> },
        { key: "clienteCodigo", label: "Código Cliente" },
        { key: "razaoSocial", label: "Razão Social" },
        { key: "nomeFantasia", label: "Nome Fantasia" },
        { key: "rota", label: "Rota" },
        { key: "cidade", label: "Cidade" },
        { key: "canal", label: "Canal" },
        { key: "gdmStatus", label: "GDM Status" },
        { key: "numeroSerie", label: "Nº de Série" },
        { key: "gdmAtivo", label: "GDM Ativo" },
        { key: "numeroContratoCev", label: "Contrato CEV" },
        { key: "logomarca", label: "Logomarca" },
        { key: "solicitacaoNumero", label: "Solicitação" },
        { key: "quantidadeSolicitacoes", label: "Qtd. Solicitações", center: true },
        { key: "statusSolicitacao", label: "Status", center: true, render: v => (v ? <StatusBadge status={v} /> : "-") },
        { key: "etapaAtual", label: "Etapa Atual" },
        { key: "solicitanteNome", label: "Solicitante" },
        { key: "dataSolicitacao", label: "Data da Solicitação", format: "date" },
        { key: "dataConclusao", label: "Data de Conclusão", format: "date" },
        { key: "justificativa", label: "Justificativa / Motivo" },
    ];

    const colunasForaDaBase = [
        { key: "solicitacaoNumero", label: "Solicitação" },
        { key: "situacao", label: "Situação", center: true, render: v => <SituacaoBadge situacao={v} /> },
        { key: "motivo", label: "Por que está fora da base" },
        { key: "clienteCodigo", label: "Código Cliente" },
        { key: "clienteRazaoSocial", label: "Razão Social" },
        { key: "rota", label: "Rota" },
        { key: "cidade", label: "Cidade" },
        { key: "numeroSerie", label: "Nº de Série" },
        { key: "gdmAtivo", label: "GDM Ativo" },
        { key: "statusSolicitacao", label: "Status", center: true, render: v => (v ? <StatusBadge status={v} /> : "-") },
        { key: "solicitanteNome", label: "Solicitante" },
        { key: "dataSolicitacao", label: "Data da Solicitação", format: "date" },
    ];

    const filtroDropdown = (campo, label, lista) => (
        <Campo label={label}>
            <CustomDropdown value={filtros[campo]} onChange={e => setFiltro(campo, e.value)}
                options={(opcoes[lista] || []).map(v => ({ value: v, label: v }))} placeholder="Todos" showClear filter />
        </Campo>
    );

    if (erroAcesso) {
        return (
            <main className="px-4 py-6">
                <div className="bg-card border border-border rounded-lg shadow-card p-6 text-sm text-muted-foreground">
                    <i className="fa fa-lock mr-2" />{erroAcesso}
                </div>
            </main>
        );
    }

    return (
        <main className="px-4 py-6 flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold text-foreground">Conclusão da Renovação de GDM</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Base de GDM x solicitações de renovação
                        {dados?.dataUltimaImportacao && ` · base importada em ${formatarDataHora(dados.dataUltimaImportacao)}`}
                        {dados && ` · ${dados.renovacoesConsideradas.toLocaleString("pt-BR")} solicitação(ões) consideradas`}
                    </p>
                </div>
                <Button variant="outline" label="Base de GDM" iconLeft="fa fa-database" onClick={() => navigate("/solicitacoes/base-gdm")} />
            </div>

                        <div className="bg-card border border-border rounded-lg shadow-card p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground">Filtros</h2>
                    <button type="button" onClick={limparFiltros} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
                        Limpar filtros
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Campo label="Solicitações de (data inicial)">
                        <input type="date" value={filtros.dataInicial} onChange={e => setFiltro("dataInicial", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Até (data final)">
                        <input type="date" value={filtros.dataFinal} onChange={e => setFiltro("dataFinal", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <div className="sm:col-span-2">
                        <Campo label="Cliente">
                            <input type="text" value={filtros.cliente} onChange={e => setFiltro("cliente", e.target.value)}
                                onKeyDown={e => e.key === "Enter" && pesquisar(filtros)}
                                placeholder="Código, razão social ou fantasia" className={INPUT_CLASS} />
                        </Campo>
                    </div>
                    {filtroDropdown("rota", "Rota", "rotas")}
                    {filtroDropdown("cidade", "Cidade", "cidades")}
                    {filtroDropdown("canal", "Canal", "canais")}
                    {filtroDropdown("gdmStatus", "GDM Status", "gdmStatus")}
                    {filtroDropdown("logomarca", "Logomarca", "logomarcas")}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                        Sem período, entram todas as solicitações. Com várias solicitações para a mesma GDM, vale a de maior peso:
                        renovada, em andamento, não renovada e, por último, reprovada/cancelada.
                    </span>
                    <Button variant="blue" label="Pesquisar" iconLeft="fa fa-search" loading={carregando} onClick={() => pesquisar(filtros)} />
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {kpis.map(kpi => (
                    <CardIndicador key={kpi.label} label={kpi.label} value={kpi.value} icon={kpi.icon} iconClassName={kpi.iconClassName}
                        loading={carregando} active={situacao === kpi.situacao && kpi.situacao !== null}
                        onClick={() => setSituacao(atual => (atual === kpi.situacao ? null : kpi.situacao))} />
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <CardIndicador label="% concluído (processo encerrado)" value={formatarPercentual(resumo.percentualConcluido)}
                    icon="fa fa-flag-checkered" iconClassName="bg-primary/10 text-primary" loading={carregando} />
                <CardIndicador label="% renovado" value={formatarPercentual(resumo.percentualRenovado)}
                    icon="fa fa-rotate" iconClassName="bg-success/10 text-success" loading={carregando} />
            </div>

            <BarraProgresso resumo={resumo} />

            <div className="flex flex-col gap-2 lg:grid lg:grid-cols-12">
                <div className="lg:col-span-12">
                    <ChartCard
                        title="GDMs por etapa"
                        view={viewEtapa}
                        onViewChange={setViewEtapa}
                        accent="#3B82F6"
                        tableContent={<TabelaEntriesGrafico entries={porEtapa.map(b => [b.label, b.total])} colunaLabel="Etapa" colunaValor="GDMs" />}
                    >
                        {resumo.total === 0 ? (
                            <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                        ) : (
                            <>
                                <Chart type="bar" height={300} series={[{ name: "GDMs", data: porEtapa.map(b => b.total) }]} options={etapaOptions} />
                                <p className="text-xs text-muted-foreground">
                                    Etapa atual da solicitação de cada GDM. Clique numa barra para filtrar a tabela abaixo.
                                </p>
                            </>
                        )}
                    </ChartCard>
                </div>

                <div className="lg:col-span-12">
                    <ChartCard
                        title={`Conclusão por ${labelAgrupamento?.toLowerCase()}`}
                        view={viewGrupo}
                        onViewChange={setViewGrupo}
                        accent="#10B981"
                        tableContent={(
                            <CustomTable data={grupos} columns={colunasGrupo} pagination pageSize={15} exportable showHeader={false}
                                className="text-[11px] border shadow-card rounded-xl" exportFilename={`conclusao_gdm_por_${agrupamento}`} footer={() => null} />
                        )}
                    >
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap gap-1">
                                {AGRUPAMENTOS.map(a => (
                                    <button key={a.value} type="button" onClick={() => setAgrupamento(a.value)}
                                        className={`px-3 py-1 rounded-md text-xs font-medium border ${agrupamento === a.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground hover:bg-muted"}`}>
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                            {grupos.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-48">Sem dados para o filtro atual.</p>
                            ) : (
                                <>
                                    <Chart type="bar" height={Math.max(260, gruposGrafico.length * 28 + 80)} series={graficoSeries} options={graficoOptions} />
                                    {limitarGrafico && (
                                        <p className="text-xs text-muted-foreground">
                                            Exibindo os {LIMITE_GRUPOS_GRAFICO} maiores de {grupos.length} — veja todos na tabela.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </ChartCard>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold text-foreground">
                        GDMs da base
                        {situacao && <> — {SITUACAO_POR_VALOR[situacao].label.toLowerCase()}</>}
                        {etapaSelecionada && <> — etapa: {etapaSelecionada.label.toLowerCase()}</>}
                        <span className="text-muted-foreground font-normal"> ({gdmsFiltradas.length.toLocaleString("pt-BR")})</span>
                    </h2>
                    {(situacao || etapa) && (
                        <button type="button" onClick={() => { setSituacao(null); setEtapa(null); }} className="text-xs text-primary hover:underline">
                            Mostrar todas
                        </button>
                    )}
                </div>
                <CustomTable
                    data={gdmsFiltradas}
                    columns={colunasGdm}
                    loading={carregando}
                    pagination
                    pageSize={20}
                    exportable
                    showHeader={false}
                    className="text-[11px] border shadow-card rounded-xl"
                    exportFilename="conclusao_renovacao_gdm"
                    footer={() => null}
                    onRowSelect={(row, e) => row.solicitacaoId && navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
                />
            </div>

            {dados?.foraDaBase?.length > 0 && (
                <div className="flex flex-col gap-2">
                    <div>
                        <h2 className="text-sm font-semibold text-foreground">
                            Solicitações fora da base <span className="text-muted-foreground font-normal">({dados.foraDaBase.length})</span>
                        </h2>
                        <p className="text-xs text-muted-foreground">
                            Renovações que não casam com nenhuma GDM da base atual (pelo nº de série ou GDM Ativo) — não entram nos números acima.
                        </p>
                    </div>
                    <CustomTable
                        data={dados.foraDaBase}
                        columns={colunasForaDaBase}
                        pagination
                        pageSize={10}
                        exportable
                        showHeader={false}
                        className="text-[11px] border shadow-card rounded-xl"
                        exportFilename="renovacoes_gdm_fora_da_base"
                        footer={() => null}
                        onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
                    />
                </div>
            )}
        </main>
    );
}
